import { useEffect, useState } from "react";
import { postRegister } from "../../api/client";
import { Button } from "../../components/Button";
import { Callout } from "../../components/Callout";
import { Rows } from "../../components/Rows";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useConfig } from "../../hooks/useConfig";
import { formatRegisteredAt, shortAddress } from "../../lib/format";
import { buildAirdropMessage } from "../../lib/message";
import {
  ROBINHOOD_CHAIN_ID,
  ROBINHOOD_EXPLORER_URL,
  ROBINHOOD_RPC_URL,
} from "../../wallets/chain";
import { WalletMissingError, walletErrorMessage } from "../../wallets/errors";
import {
  connectEvm,
  ensureRobinhoodChain,
  hasEthereum,
  personalSignEvm,
  readConnectedEvm,
  requestEvmAccountChange,
} from "../../wallets/metamask";
import { hasPhantom, signMessageWithPhantom } from "../../wallets/phantom";
import { StepShell } from "./StepShell";
import type { AirdropFlow } from "./useAirdropFlow";

export function StepRegister({ flow }: { flow: AirdropFlow }) {
  const { config } = useConfig();
  const [done, setDone] = useState(false);
  const eligibility = flow.eligibility;
  const open = config?.phase === "registration";
  const chainId = config?.chainId ?? ROBINHOOD_CHAIN_ID;
  const rpcUrl = config?.rpcUrl ?? ROBINHOOD_RPC_URL;
  const explorerUrl = config?.explorerUrl ?? ROBINHOOD_EXPLORER_URL;

  // Pick up an already-authorised MetaMask account without prompting.
  useEffect(() => {
    let live = true;
    void readConnectedEvm().then((address) => {
      if (live && address) flow.setEvmAddress(address);
    });
    return () => {
      live = false;
    };
    // Runs once. flow.setEvmAddress is a stable state setter.
  }, [flow.setEvmAddress]);

  const onWalletMissing = (error: unknown, fallback: string): string => {
    if (error instanceof WalletMissingError) {
      flow.setMissingWallet(error.wallet);
      return "";
    }
    return walletErrorMessage(error, fallback);
  };

  const connect = useAsyncAction(async (pick: boolean) => {
    if (!hasEthereum()) throw new WalletMissingError("metamask");
    const address = pick ? await requestEvmAccountChange() : await connectEvm();
    await ensureRobinhoodChain(chainId, rpcUrl, explorerUrl);
    flow.setEvmAddress(address);
    setDone(false);
    return address;
  }, (error) => onWalletMissing(error, "Could not connect MetaMask."));

  const register = useAsyncAction(async () => {
    if (!eligibility) throw new Error("Check eligibility first.");
    const evmAddress = flow.evmAddress;
    if (!evmAddress) throw new Error("Connect MetaMask first.");
    if (!hasPhantom()) throw new WalletMissingError("phantom");

    // One template, signed by both wallets, issued now. The server re-derives
    // it from the posted fields and rejects anything older than 15 minutes.
    const message = buildAirdropMessage(eligibility.solanaAddress, evmAddress);
    const solanaSignature = await signMessageWithPhantom(message, eligibility.solanaAddress);
    await ensureRobinhoodChain(chainId, rpcUrl, explorerUrl);
    const evmSignature = await personalSignEvm(message, evmAddress);

    const result = await postRegister({
      solanaAddress: eligibility.solanaAddress,
      solanaMessage: message,
      solanaSignature,
      evmAddress,
      evmMessage: message,
      evmSignature,
    });
    setDone(true);
    await flow.refresh();
    return result;
  }, (error) => onWalletMissing(error, "Registration failed."));

  const error = connect.error || register.error || null;
  const registration = eligibility?.registration ?? null;
  const evmAddress = flow.evmAddress;
  const alreadyThis =
    registration !== null &&
    evmAddress !== null &&
    registration.evmAddress.toLowerCase() === evmAddress.toLowerCase();

  return (
    <StepShell number={2} title="Register an EVM address" active={eligibility !== null}>
      {!eligibility ? (
        <p className="body">Check eligibility first. Registration opens once a wallet is found.</p>
      ) : (
        <>
          <p className="body">
            You sign one message with Phantom and the same message with MetaMask. That proves the
            two wallets belong to the same person. The MetaMask address is where $TISM is sent.
          </p>

          {registration ? (
            <div className="card card--sunken">
              <Rows
                items={[
                  {
                    key: "evm",
                    label: "Registered address",
                    value: <span className="mono">{registration.evmAddress}</span>,
                    stack: true,
                  },
                  {
                    key: "at",
                    label: "Registered",
                    value: formatRegisteredAt(registration.registeredAt),
                  },
                ]}
              />
            </div>
          ) : null}

          {!open ? (
            <Callout tone="neutral" role="status">
              Registration is closed. The list is fixed as it stands, so a registered address can no
              longer be changed.
            </Callout>
          ) : (
            <>
              <div className="form-actions">
                <Button
                  variant={evmAddress ? "ghost" : "primary"}
                  onClick={() => void connect.run(evmAddress !== null)}
                  pending={connect.pending}
                >
                  {evmAddress ? "Use a different address" : "Connect MetaMask"}
                </Button>
                {evmAddress ? (
                  <span className="btn__note mono">{shortAddress(evmAddress, 8, 6)}</span>
                ) : null}
              </div>

              <Button
                onClick={() => void register.run()}
                disabled={!evmAddress || alreadyThis}
                pending={register.pending}
              >
                {registration ? "Sign and update" : "Sign and register"}
              </Button>

              {alreadyThis ? (
                <p className="tiny">
                  This address is already registered. Pick a different one in MetaMask to change it.
                </p>
              ) : (
                <p className="tiny">
                  You can change the address any time while registration is open.
                </p>
              )}
            </>
          )}

          {done && !error ? (
            <Callout role="status">Registered. Come back here when claims open.</Callout>
          ) : null}

          {error ? (
            <Callout tone="error" role="alert">
              {error}
            </Callout>
          ) : null}
        </>
      )}
    </StepShell>
  );
}
